












import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useOneTimePurchase from "@/hooks/useOneTimePurchase";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { PurchaseDetails } from "@/payments/types";
import { ContentFeedPurchaseEvent } from "@/types";
import { EVENTS } from "@/const";
import UnlockFeedView from "./components/UnlockFeedView.vue";

export default defineComponent({
  name: "UnlockFeed",
  components: { UnlockFeedView },
  setup() {
    const loading = ref(false);
    const purchaseProcessing = ref(false);
    const selectedFeed = ref<ContentFeed | null>(null);
    const paymentPackages = ref<PaymentPackage[]>([]);
    const publicPaymentSystems = ref<PaymentSystemPublic[]>([]);
    const typeOfPurchase = ref<Omit<ContentFeedPurchaseEvent, "feed"> | undefined>();

    const mainCurrency = computed(() => getMainCurrencyCode());
    const paymentSystemId = computed(
      () => publicPaymentSystems.value.find(publicSystem => publicSystem.currencies.includes(mainCurrency.value))?.id
    );
    const cost = computed(
      () =>
        paymentPackages.value
          .find(p => p.id === selectedFeed.value?.pricePackageId)
          ?.costs?.find(c => c.currency === getMainCurrencyCode())?.cost
    );
    const data = computed<ContentPriceSubject>(() => ({
      subjectId: selectedFeed.value?.id ?? "0",
      subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
    }));
    const purchaseDetails = computed<PurchaseDetails>(() => ({
      pricePackageId: selectedFeed.value?.pricePackageId ?? "0",
      price: (cost.value && formatMoneyString(cost.value, { currencyCode: getMainCurrencyCode() })) || undefined,
      subject: {
        subjectId: selectedFeed.value?.id ?? "0",
        subjectType: PurchaseSubjectTypeEnum.CONTENT_SET,
      },
    }));
    const { performOneTimePurchase, purchaseInfoDetails } = useOneTimePurchase(data, purchaseDetails);

    const handleOneTimePurchase = async (selectedFeedByUser?: ContentFeed) => {
      if (!selectedFeedByUser || !paymentSystemId.value) return;
      selectedFeed.value = selectedFeedByUser;
      try {
        purchaseProcessing.value = true;
        const response = await performOneTimePurchase();
        if (response.success) {
          emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, {
            userId: selectedFeed.value.userId,
            feedId: selectedFeed.value.id,
          });
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("One time buy {0} content for {1}", [
              selectedFeed.value.user.screenName,
              cost.value ? formatMoneyString(cost.value, { currencyCode: getMainCurrencyCode() }) : "0",
            ]),
            image: {
              user: selectedFeed.value.user,
            },
            button: {
              text: $t("Close"),
              cb: () => {},
            },
            position: "top",
          });
          selectedFeed.value = null;
        }
      } finally {
        purchaseProcessing.value = false;
      }
    };

    const showUnlockFeed = async (eventData?: ContentFeedPurchaseEvent) => {
      if (loading.value || !eventData) return;
      const { feed, ...others } = eventData;
      typeOfPurchase.value = others;
      try {
        loading.value = true;
        paymentPackages.value = await providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] });
        selectedFeed.value = feed;
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      selectedFeed.value = null;
    };

    providers.PaymentSystemsProvider.findAllPublic().then(r => (publicPaymentSystems.value = r));
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASE, showUnlockFeed);
    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASE, showUnlockFeed);
    });

    return {
      selectedFeed,
      paymentPackages,
      loading,
      purchaseProcessing,
      purchaseInfoDetails,
      typeOfPurchase,
      handleOneTimePurchase,
      handleClose,
    };
  },
});
